import React, { useState, useEffect } from 'react';
import Home from './Home';
import Edcat from 'services/edcat';
import { Error, Loading, AbsoluteCenter } from 'components';
import { useStore } from 'store';

export default function HomeContainer(props) {
  const [settings] = useStore('settings');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [homeData, setHomeData] = useState({});
  const [loadingState, setLoadingState] = useState({
    meta: true,
    featuredSection: true,
    items: true,
    events: true,
    stories: true,
    entities: true
  });

  const fetchHomeData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Step 1: Fetch initial data in parallel (meta, featured section, entities)
      const [metaResponse, featuredSectionResponse, entitiesResponse] = await Promise.all([
        Edcat.homePageAPIs.getMeta(),
        Edcat.homePageAPIs.getFeaturedSection(),
        Edcat.homePageAPIs.getEntities()
      ]);
      
      setHomeData(prevData => ({
        ...prevData,
        total_items: metaResponse.total_items,
        bottom_section: metaResponse.bottom_section,
        featured_section: featuredSectionResponse,
        entities: entitiesResponse.entities
      }));
      
      setLoadingState(prev => ({
        ...prev,
        meta: false,
        featuredSection: false,
        entities: false
      }));
      
      // Step 2: Get IDs to exclude from featured section
      const excludeItemIds = featuredSectionResponse._meta?.added_items_ids || [];
      const excludeStoryIds = featuredSectionResponse._meta?.added_stories_ids || [];
      const excludeEventIds = featuredSectionResponse._meta?.added_events_ids || [];
      
      // Step 3: Fetch remaining data in parallel, excluding featured items
      const [itemsResponse, eventsResponse, storiesResponse] = await Promise.all([
        Edcat.homePageAPIs.getItems(excludeItemIds),
        Edcat.homePageAPIs.getEvents(excludeEventIds),
        Edcat.homePageAPIs.getStories(excludeStoryIds)
      ]);
      
      setHomeData(prevData => ({
        ...prevData,
        items: itemsResponse.items,
        events: eventsResponse.events,
        stories: storiesResponse.stories
      }));
      
      setLoadingState({
        meta: false,
        featuredSection: false,
        items: false,
        events: false,
        stories: false,
        entities: false
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching home data:', err);
      setError(err);
      setLoading(false);
    }
  };

  const refetch = () => {
    setLoadingState({
      meta: true,
      featuredSection: true,
      items: true,
      events: true,
      stories: true,
      entities: true
    });
    fetchHomeData();
  };

  useEffect(() => {
    fetchHomeData();
  }, []);

  if (loading && Object.values(loadingState).every(state => state === true)) {
    return (
      <AbsoluteCenter>
        <Loading center size={80} width={6} />
      </AbsoluteCenter>
    );
  }

  if (error) {
    return <Error error={error} retry={refetch} />;
  }

  return (
    <Home
      {...props}
      data={homeData}
      allPublicationsUrl={settings && settings.all_publications_url}
      loadingState={loadingState}
    />
  );
}
